/**
 * `<koko-companion>` custom element. Hosts the KokoApp inside a closed
 * shadow root so host-page CSS/JS can't collide with the widget and
 * vice versa. Attributes map 1:1 to the KokoConfig surface.
 */

import shadowStyles from "./styles/shadow.css?raw";
import { KokoApp } from "./app/KokoApp";
import type { KokoConfig, KokoExtensions } from "./types";
import type { KokoActionAgent } from "./agent/KokoActionAgent";
import { preloadAllSpriteFrames } from "./sprite/preload";
import {
  buildBirdStyleOptions,
  fetchBirdManifest,
  findBirdVariant,
  type BirdStyleOption,
} from "./sprite/birdVariants";
import { loadKokoFont } from "./ui/loadFont";

const DEFAULT_GREETING = "Ask Koko...";

export class KokoElement extends HTMLElement {
  static get observedAttributes(): string[] {
    return [
      "api",
      "voice",
      "greeting",
      "model",
      "sprite-base-url",
      "bird-base-url",
      "bird-variant",
      "font-base-url",
      "audio-base-url",
    ];
  }

  private kokoApp: KokoApp | null = null;
  private resolvedSpriteBaseUrl = "";
  private resolvedBirdBaseUrl = "";
  private resolvedFontBaseUrl = "";
  private resolvedAudioBaseUrl = "";
  private mounting = false;
  private mountRequestId = 0;
  private warnedBirdFallback = false;
  /**
   * Extensions registered before the element was upgraded into the
   * DOM. Stashed here so we can apply them in `connectedCallback`
   * once the inner KokoApp exists. Without this, a host page that
   * calls `element.setExtensions(...)` before the element finishes
   * its first connect would silently lose the registration.
   */
  private pendingExtensions: KokoExtensions | null = null;

  connectedCallback(): void {
    if (this.kokoApp || this.mounting) return;

    const config = this.readConfigFromAttributes();
    if (!config) {
      console.warn(
        "[koko] <koko-companion> requires an `api` attribute pointing at the Cloudflare Worker. Widget not mounted.",
      );
      return;
    }

    this.mounting = true;
    const requestId = ++this.mountRequestId;
    void this.mountBirdStyles(config, requestId);
  }

  private async mountBirdStyles(
    config: KokoConfig,
    requestId: number,
  ): Promise<void> {
    const requestedStyleId = this.getAttribute("bird-variant") ?? "pixel";
    let birdStyles: readonly BirdStyleOption[] = buildBirdStyleOptions(null);
    let defaultBirdStyleId = "pixel";
    try {
      const manifest = await fetchBirdManifest(this.resolvedBirdBaseUrl);
      birdStyles = buildBirdStyleOptions(manifest);
      if (requestedStyleId !== "pixel") {
        const variant = findBirdVariant(manifest, requestedStyleId);
        if (!variant) {
          throw new Error(`variant "${requestedStyleId}" was not found`);
        }
        defaultBirdStyleId = requestedStyleId;
      }
    } catch (error) {
      if (requestedStyleId !== "pixel") {
        this.warnBirdFallback(requestedStyleId, error);
      }
      birdStyles = buildBirdStyleOptions(null);
      defaultBirdStyleId = "pixel";
    }

    if (requestId !== this.mountRequestId) return;
    this.mounting = false;
    if (!this.isConnected || this.kokoApp) return;
    this.mountApp(config, { birdStyles, defaultBirdStyleId });
  }

  private mountApp(
    config: KokoConfig,
    birdStyles: {
      birdStyles: readonly BirdStyleOption[];
      defaultBirdStyleId: string;
    },
  ): void {
    // `open` so tests (and site owners who want to introspect) can walk
    // the shadow tree. Style encapsulation still works identically in
    // both modes — the visual isolation is what matters.
    const shadow = this.attachShadow({ mode: "open" });
    const styleEl = document.createElement("style");
    styleEl.textContent = shadowStyles;
    shadow.appendChild(styleEl);

    // Register the pixel font on `document.fonts` so the shadow root can
    // reference it by name — @font-face inside a shadow tree is not
    // reliably supported (Safari ignores it entirely).
    void loadKokoFont(
      `${this.resolvedFontBaseUrl.replace(/\/$/, "")}/PokemonDPPt.ttf`,
    );

    // Preload sprite frames so the first flap isn't a network stutter.
    preloadAllSpriteFrames(this.resolvedSpriteBaseUrl);

    this.kokoApp = new KokoApp(shadow, config, {
      spriteBaseUrl: this.resolvedSpriteBaseUrl,
      audioBaseUrl: this.resolvedAudioBaseUrl,
      birdBaseUrl: this.resolvedBirdBaseUrl,
      birdStyles: birdStyles.birdStyles,
      defaultBirdStyleId: birdStyles.defaultBirdStyleId,
    });
    if (this.pendingExtensions) {
      this.kokoApp.setExtensions(this.pendingExtensions);
    }
    void this.kokoApp.start();
  }

  disconnectedCallback(): void {
    this.mountRequestId += 1;
    this.mounting = false;
    this.kokoApp?.destroy();
    this.kokoApp = null;
  }

  /**
   * Public API for host pages: register custom tools, system-prompt
   * additions, and directive handlers. May be called before or after
   * the element is upgraded — buffered registrations are applied as
   * soon as the inner KokoApp comes online. Pass `null` to clear.
   */
  setExtensions(extensions: KokoExtensions | null): void {
    this.pendingExtensions = extensions;
    this.kokoApp?.setExtensions(extensions);
  }

  private readConfigFromAttributes(): KokoConfig | null {
    const api = this.getAttribute("api");
    if (!api) return null;

    const voiceAttr = this.getAttribute("voice");
    const voice = voiceAttr === null ? true : voiceAttr !== "off" && voiceAttr !== "false";

    const greeting = this.getAttribute("greeting") ?? DEFAULT_GREETING;
    const model = this.getAttribute("model") ?? undefined;

    this.resolvedSpriteBaseUrl =
      this.getAttribute("sprite-base-url") ??
      this.inferAssetBaseUrl(api, "sprites");
    this.resolvedBirdBaseUrl =
      this.getAttribute("bird-base-url") ??
      this.inferAssetBaseUrl(api, "birds");
    this.resolvedFontBaseUrl =
      this.getAttribute("font-base-url") ??
      this.inferAssetBaseUrl(api, "fonts");
    this.resolvedAudioBaseUrl =
      this.getAttribute("audio-base-url") ??
      this.inferAssetBaseUrl(api, "audio");

    return { api, voice, greeting, model };
  }

  private warnBirdFallback(variantId: string, error: unknown): void {
    if (this.warnedBirdFallback) return;
    this.warnedBirdFallback = true;
    const detail = error instanceof Error ? error.message : String(error);
    console.warn(
      `[koko] bird variant "${variantId}" unavailable (${detail}); falling back to pixel sprites.`,
    );
  }

  /**
   * Resolve a sibling asset folder (sprites/fonts/audio) relative to
   * wherever koko.js was loaded from. In dev the harness sets explicit
   * URLs; in prod the CDN build publishes these folders next to koko.js.
   */
  private inferAssetBaseUrl(apiBase: string, folder: string): string {
    const currentScript = findCurrentScriptSrc();
    if (currentScript) {
      try {
        const url = new URL(currentScript);
        if (/koko\.js$/i.test(url.pathname)) {
          url.pathname = url.pathname.replace(/\/[^/]*$/, `/${folder}`);
          url.search = "";
          return url.toString();
        }
      } catch {
        // Fall through.
      }
    }
    return `${apiBase.replace(/\/$/, "")}/${folder}`;
  }

  /**
   * Public accessor for the action agent — fly to element, type into
   * input, click button, perch. Returns null if the element hasn't
   * been upgraded yet (the inner KokoApp doesn't exist). Host pages
   * typically call this once they know the element is connected.
   */
  getActionAgent(): KokoActionAgent | null {
    return this.kokoApp?.getActionAgent() ?? null;
  }

  /**
   * Flash a small status pill next to the bird (e.g. "+ mei",
   * "✓ storyboard ready"). Safe to call before the inner KokoApp
   * is online — silently no-ops in that case.
   */
  flashPill(text: string, variant: "standard" | "error" = "standard"): void {
    this.kokoApp?.flashPill(text, variant);
  }

  /**
   * After a host-page choreography completes, hand the list of
   * actions Koko just performed to KokoApp; it fires a follow-up
   * Claude call that streams a short conversational reply into
   * the response bubble (and speaks it via TTS). Returns a
   * promise that resolves when the report-back is done; safe to
   * fire-and-forget.
   */
  async reportBack(actionsCompleted: readonly string[]): Promise<void> {
    await this.kokoApp?.reportBack(actionsCompleted);
  }
}

function findCurrentScriptSrc(): string | null {
  if (document.currentScript instanceof HTMLScriptElement) {
    return document.currentScript.src || null;
  }
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    'script[src*="koko"]',
  );
  if (scripts.length > 0) {
    return scripts[scripts.length - 1].src || null;
  }
  return null;
}
